import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';

const emptyTelegram = { tgNumber: '', tgID: '', tgName: '' };
const inputClassName = 'w-full px-3 py-2 border border-gray-300 rounded-md text-blue-700 font-bold';

const toMonthInputValue = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return '';
    return d.toISOString().slice(0, 7); // YYYY-MM
};

const normalizePhoneOnChange = (value) => {
    const compact = String(value || '').replace(/\s+/g, '').trim();
    if (/^\d{8}$/.test(compact)) return `+65${compact}`;
    if (/^65\d{8}$/.test(compact)) return `+${compact}`;
    return compact;
};

const SimonCustomerModal = ({ mode = 'create', customer = null, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        customerID: '',
        createMMYY: '',
        names: [''],
        phoneNumbers: [''],
        whatsappNumbers: [''],
        addresses: [''],
        telegrams: [{ ...emptyTelegram }]
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (mode === 'edit' && customer) {
            setFormData({
                customerID: customer.customerID || '',
                createMMYY: toMonthInputValue(customer.createMMYY),
                names: customer.names?.length ? customer.names : [''],
                phoneNumbers: customer.phoneNumbers?.length ? customer.phoneNumbers : [''],
                whatsappNumbers: customer.whatsappNumbers?.length ? customer.whatsappNumbers : [''],
                addresses: customer.addresses?.length ? customer.addresses : [''],
                telegrams: customer.telegrams?.length ? customer.telegrams : [{ ...emptyTelegram }]
            });
        }
    }, [mode, customer]);

    const updateArrayField = (fieldName, index, value) => {
        const next = [...formData[fieldName]];
        const normalizedValue = ['phoneNumbers', 'whatsappNumbers'].includes(fieldName)
            ? normalizePhoneOnChange(value)
            : value;
        next[index] = normalizedValue;
        setFormData((prev) => ({ ...prev, [fieldName]: next }));
    };

    const addArrayField = (fieldName, defaultValue = '') => {
        setFormData((prev) => ({ ...prev, [fieldName]: [...prev[fieldName], defaultValue] }));
    };

    const removeArrayField = (fieldName, index, defaultValue = '') => {
        const next = formData[fieldName].filter((_, itemIndex) => itemIndex !== index);
        setFormData((prev) => ({ ...prev, [fieldName]: next.length ? next : [defaultValue] }));
    };

    const updateTelegramField = (index, field, value) => {
        const next = [...formData.telegrams];
        const normalizedValue = field === 'tgNumber' ? normalizePhoneOnChange(value) : value;
        next[index] = { ...(next[index] || emptyTelegram), [field]: normalizedValue };
        setFormData((prev) => ({ ...prev, telegrams: next }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const payload = {
                customerID: formData.customerID.trim(),
                createMMYY: formData.createMMYY ? `${formData.createMMYY}-01` : undefined,
                names: formData.names.map((item) => item.trim()).filter(Boolean),
                phoneNumbers: formData.phoneNumbers.map((item) => item.trim()).filter(Boolean),
                whatsappNumbers: formData.whatsappNumbers.map((item) => item.trim()).filter(Boolean),
                addresses: formData.addresses.map((item) => item.trim()).filter(Boolean),
                telegrams: formData.telegrams
                    .map((item) => ({
                        tgNumber: item.tgNumber?.trim() || undefined,
                        tgID: item.tgID?.trim() || undefined,
                        tgName: item.tgName?.trim() || undefined
                    }))
                    .filter((item) => item.tgNumber || item.tgID || item.tgName)
            };

            const token = localStorage.getItem('token');
            const request = mode === 'edit'
                ? axios.put(`${process.env.REACT_APP_API_URL}/api/simoncustomers/${customer._id}`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                })
                : axios.post(`${process.env.REACT_APP_API_URL}/api/simoncustomers`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                });

            const response = await request;
            if (response.data.success) {
                onSuccess(response.data.data);
            }
        } catch (err) {
            setError(`Failed to ${mode} Simon customer: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-5xl my-8 min-w-0 overflow-x-hidden">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{mode === 'edit' ? 'Edit Simon Customer' : 'Create Simon Customer'}</h2>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" aria-label="Close modal">
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Customer ID *</label>
                            <input
                                type="text"
                                required
                                value={formData.customerID}
                                onChange={(e) => setFormData((prev) => ({ ...prev, customerID: e.target.value }))}
                                className={inputClassName}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Create MMYY</label>
                            <input
                                type="month"
                                value={formData.createMMYY}
                                onChange={(e) => setFormData((prev) => ({ ...prev, createMMYY: e.target.value }))}
                                className={inputClassName}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {[
                                ['names', 'Names'],
                                ['phoneNumbers', 'Phone Numbers'],
                                ['whatsappNumbers', 'WhatsApp Numbers']
                            ].map(([fieldName, label]) => (
                                <div key={fieldName}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                                    {formData[fieldName].map((item, index) => (
                                        <div key={`${fieldName}-${index}`} className="flex gap-2 mb-2">
                                            <input
                                                type="text"
                                                value={item}
                                                onChange={(e) => updateArrayField(fieldName, index, e.target.value)}
                                                className={inputClassName}
                                            />
                                            {formData[fieldName].length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeArrayField(fieldName, index, '')}
                                                    className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => addArrayField(fieldName, '')}
                                        className="mt-1 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                    >
                                        + Add {label.slice(0, -1)}
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Telegrams</label>
                            {formData.telegrams.map((item, index) => (
                                <div key={`telegram-${index}`} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                                    <input
                                        type="text"
                                        placeholder="TG Number"
                                        value={item.tgNumber || ''}
                                        onChange={(e) => updateTelegramField(index, 'tgNumber', e.target.value)}
                                        className={inputClassName}
                                    />
                                    <input
                                        type="text"
                                        placeholder="TG ID"
                                        value={item.tgID || ''}
                                        onChange={(e) => updateTelegramField(index, 'tgID', e.target.value)}
                                        className={inputClassName}
                                    />
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="TG Name"
                                            value={item.tgName || ''}
                                            onChange={(e) => updateTelegramField(index, 'tgName', e.target.value)}
                                            className={`flex-1 ${inputClassName}`}
                                        />
                                        {formData.telegrams.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeArrayField('telegrams', index, { ...emptyTelegram })}
                                                className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => addArrayField('telegrams', { ...emptyTelegram })}
                                className="mt-1 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            >
                                + Add Telegram
                            </button>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Addresses</label>
                            {formData.addresses.map((item, index) => (
                                <div key={`addresses-${index}`} className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={item}
                                        onChange={(e) => updateArrayField('addresses', index, e.target.value)}
                                        className={inputClassName}
                                    />
                                    {formData.addresses.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeArrayField('addresses', index, '')}
                                            className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => addArrayField('addresses', '')}
                                className="mt-1 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            >
                                + Add Address
                            </button>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            disabled={loading}
                        >
                            {loading ? (mode === 'edit' ? 'Updating...' : 'Creating...') : (mode === 'edit' ? 'Update' : 'Create')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SimonCustomerModal;
